import { Background, PreRequisiteSetup } from '@/src/__mocks__/mock-jam-challenge-tasks';
import { LoadingBar } from '@/src/components/common/LoadingBar';
import { Markdown } from '@/src/components/common/Markdown';
import ChallengeNotCompleteModal from '@/src/components/ui/molecules/MyJams/JamChallengeDetails/JamTask/Modals/ChallengeNotCompleteModal';
import ChallengeSolvedModal from '@/src/components/ui/molecules/MyJams/JamChallengeDetails/JamTask/Modals/ChallengeSolvedModal/ChallengeSolvedModal';
import TaskPointContainer from '@/src/components/ui/molecules/MyJams/JamChallengeDetails/JamTask/PointsContainer';
import GameTaskClues from '@/src/components/ui/organisms/MyJams/JamChallengeDetails/GameTaskClues';
import { JAM_CHALLENGE_DETAILS_ROUTES, JAM_CHALLENGE_TASK_ROUTES } from '@/src/routes';
import { useFlashbars } from '@/src/store/flashbar.context';
import { useJamChallengeDetails } from '@/src/store/jam-challenge-details.context';
import { useJamChallenge } from '@/src/store/jam-challenge.context';
import { useJamEventDetails } from '@/src/store/jam-event-details.context';
import { JamTaskProgress, JamTaskStatus } from '@/src/types/JamChallengeDetails';
import { JamChallengeTaskAnswerFields } from '@/src/types/JamChallenges';
import { i18nKeys } from '@/src/utils/i18n.utils';
import { jamChallengeTaskAnswerValidator } from '@/src/utils/jam-challenge-task.validation.utils';
import { preProdLogger } from '@/src/utils/log.utils';
import { LoggingService } from '@/src/utils/logging-service.utils';
import {
  Box,
  Button,
  Container,
  ContentLayout,
  Form,
  FormField,
  Header,
  Input,
  SpaceBetween,
  Tabs,
} from '@amzn/awsui-components-react';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router-dom';
import JamChallengeHeader from '../../JamChallengeHeader/JamChallengeHeader';
import JamTaskAuditModal from './JamTaskAuditModal';
import './JamTaskInfo.scss';
import JamTaskStatusModal from './JamTaskStatusModal';

import { GenAiAuditOptions } from '@/src/api/GenAiAPI';
import { useApi } from '@/src/store/api.context';

interface IHandleOnChange {
  detail: { value: string };
}

export const JamTaskInfo: React.FC = () => {
  const { t } = useTranslation();
  const {
    selectedJamChallenge,
    isFetchingJamChallengeDetails,
    validateTaskAnswer,
    getJamChallengeTask,
    task,
    fetchingtaskValidationResponse,
    challengeProgress,
    isSelectedChallengeStarted,
    challengeSupportDetails,
    loadJamChallengeProgressData,
    jamChallengeId,
    loadJamChallengeDetails,
    loadChallengeSupportDetails,
    showChallengeStatusProgress,
    taskValidationResponse,
  } = useJamChallengeDetails();
  const { eventName, eventSlug, event } = useJamEventDetails();
  const { loadJamChallengeData } = useJamChallenge();
  const params: { 0: string; 1: string; 2: string } = useParams();
  const history = useHistory();
  const { clearFlashbars, addErrorFlashbar } = useFlashbars();
  const [answer, setAnswer] = useState<string>('');
  const [answerError, setAnswerError] = useState<string>('');
  const [loading, setLoading] = useState(false);
  // A failed fetch still counts as "settled" instead of waiting forever for challengeProgress.
  const [progressResolvedChallengeId, setProgressResolvedChallengeId] = useState<string>();
  const [showProgressModal, showProgressModalSet] = useState(false);
  const [showAuditModal, setShowAuditModal] = useState(false);
  const [disableAuditButton, setDisableAuditButton] = useState(false);
  const [loadingAudit, setLoadingAudit] = useState(false);
  const [auditMessage, setAuditMessage] = useState('');
  const [validationMessage, setValidationMessage] = useState<string>('');
  const [isUserInitiatedValidation, setIsUserInitiatedValidation] = useState(false);
  const { genAiAPI } = useApi();

  const handleSolvedModalClose = useCallback(() => {
    showProgressModalSet(false);
    void loadJamChallengeDetails({ id: eventName, challengeId: params[1] });
    void loadJamChallengeData({ id: eventName, showLoader: false });
    history.push(JAM_CHALLENGE_DETAILS_ROUTES.Overview.resolve([eventSlug, params[1]]));
  }, [history, eventSlug, params]);

  const handleModalDismiss = () => {
    showProgressModalSet(false);
  };

  const isFacilitator = !!event?.facilitator;
  const locked = !!selectedJamChallenge?.locked || !!selectedJamChallenge?.awaitingLabAssignment;
  const briefClues = task?.clues || [];
  const previousTaskProgress = useRef<JamTaskProgress | undefined>();

  const currentTaskProgress = useMemo(() => {
    if (challengeProgress?.taskProgress?.length) {
      const taskProgress = challengeProgress?.taskProgress.find(({ taskId }) => taskId === task?.id);
      return taskProgress;
    }
  }, [challengeProgress, task?.id]);

  // Keyed off URL task id, not lagging `task` state. A same-challenge task switch computes the current task's lock.
  const currentTaskLocked = !!challengeProgress?.taskProgress?.find(({ taskId }) => taskId === params[2])?.locked;
  // Only facilitators, or a viewer on an unlocked, started challenge whose current task isn't sequentially locked.
  const canViewContent = isFacilitator || (!locked && isSelectedChallengeStarted && !currentTaskLocked);

  // Resolve the URL task from selectedJamChallenge.tasks to tell "loaded but absent" from "not yet loaded".
  const currentTaskExists = !!selectedJamChallenge?.tasks?.some((taskItem) => taskItem.id === params[2]);

  // Settled once progress matched or completed AND loaded challenge matches URL
  const progressSettled =
    !!selectedJamChallenge &&
    selectedJamChallenge.id === params[1] &&
    (challengeProgress?.challengeId === selectedJamChallenge.id || progressResolvedChallengeId === params[1]);

  const clues = useMemo(() => {
    if (showChallengeStatusProgress) {
      return [];
    }
    if (!isFacilitator) {
      return briefClues || [];
    }
    // Return brief clues while support data loads so entries stay visible; full text self-heals once challengeSupportDetails resolves.
    if (!challengeSupportDetails) {
      return briefClues || [];
    }
    const challenge = challengeSupportDetails.challenges.find(({ id }) => id === selectedJamChallenge?.id);
    if (!challenge) {
      return briefClues || [];
    }
    const taskDetails = challenge.getTaskDetails(task?.id as string);
    // Participant payload has clues stripped - source preview clues from facilitator support details.
    if (selectedJamChallenge?.awaitingLabAssignment && taskDetails?.clues?.length) {
      return taskDetails.clues.map((clue) => ({
        order: clue.order,
        title: clue.title,
        description: clue.description,
        cluePenalty: clue.penalty,
        used: false,
      }));
    }
    return briefClues.map((clue) => ({
      ...clue,
      description: taskDetails?.getClue(clue.order)?.description,
    }));
  }, [
    briefClues,
    isFacilitator,
    challengeSupportDetails,
    selectedJamChallenge,
    task,
    currentTaskProgress?.completed,
    showChallengeStatusProgress,
  ]);

  // Clues came from the facilitator support-details fallback
  // Clues are preview-only and Unlock is disabled until the lab is assigned
  const cluesFromSupportFallback = isFacilitator && !!selectedJamChallenge?.awaitingLabAssignment && clues.length > 0;

  const handleInput = ({ detail }: IHandleOnChange) => {
    setAnswer(detail.value);
  };

  // Update validation message when taskValidationResponse changes
  useEffect(() => {
    if (taskValidationResponse) {
      // Use localizedMessage if present, otherwise fall back to message
      setValidationMessage(taskValidationResponse.localizedMessage || taskValidationResponse.message || '');
    } else {
      // Clear validation message when no response (allows modal to use default message)
      setValidationMessage('');
    }
  }, [taskValidationResponse]);

  const handleProgressCheck = useCallback(async () => {
    try {
      setIsUserInitiatedValidation(true); // Flag that this is user-initiated
      // First call the validate API with empty string as the answer
      await validateTaskAnswer({ id: eventName, challengeId: jamChallengeId }, '', task?.id);

      // Then continue with existing flow
      await loadJamChallengeProgressData({ id: eventName, challengeId: jamChallengeId });
      showProgressModalSet(true);
    } catch (err) {
      preProdLogger('Progress Check failed', err);
      setValidationMessage('');
      showProgressModalSet(true);
    } finally {
      setIsUserInitiatedValidation(false); // Reset flag
    }
  }, [validateTaskAnswer, loadJamChallengeProgressData, eventName, jamChallengeId, task]);

  useEffect(() => {
    if (!selectedJamChallenge?.awsLabAccount) {
      setDisableAuditButton(true);
    }
  }, [selectedJamChallenge?.awsLabAccount]);

  const handleAiTaskAudit = useCallback(async () => {
    preProdLogger(
      `{
  "eventId": "${eventName}",
  "challengeId": "${jamChallengeId}",
  "accountNumber": "${selectedJamChallenge?.awsLabAccount}",
  "taskNumber": "${task?.taskNumber}"
}`
    );
    if (selectedJamChallenge && task) {
      setLoadingAudit(true);
      setDisableAuditButton(true);
      preProdLogger('Sending audit request');
      const options: GenAiAuditOptions = {
        eventId: eventName,
        challengeId: jamChallengeId,
        accountNumber: selectedJamChallenge?.awsLabAccount,
        taskNumber: task?.taskNumber.toString(),
      };

      try {
        const response = await genAiAPI.createNewAuditRequest(options);
        setAuditMessage(response.auditResult);
        setShowAuditModal(true);
        preProdLogger(`Audit response: ${JSON.stringify(response)}`);
      } finally {
        setLoadingAudit(false);
      }
    }
  }, [jamChallengeId, task, eventName, selectedJamChallenge]);

  useEffect(() => {
    // only show modal when task complete goes from false to true
    if (
      currentTaskProgress?.completed &&
      previousTaskProgress?.current?.completed === false &&
      previousTaskProgress?.current?.taskId === currentTaskProgress.taskId &&
      !!currentTaskProgress.taskId &&
      !isUserInitiatedValidation // Only call validate if NOT user-initiated
    ) {
      const fetchCompletionMessage = async () => {
        try {
          await validateTaskAnswer({ id: eventName, challengeId: jamChallengeId }, '', task?.id);
        } catch (err) {
          LoggingService.warn('Failed to fetch completion message:', err);
        } finally {
          showProgressModalSet(true);
        }
      };

      void fetchCompletionMessage();
    }
    previousTaskProgress.current = currentTaskProgress;
  }, [currentTaskProgress, isUserInitiatedValidation, validateTaskAnswer, eventName, jamChallengeId, task?.id]);

  const submitAnswer = async (input: string) => {
    clearFlashbars();
    setLoading(true);
    setIsUserInitiatedValidation(true); // Flag that this is user-initiated
    const success = await validateTaskAnswer({ id: eventName, challengeId: jamChallengeId }, input, task?.id);
    setAnswer('');
    showProgressModalSet(true);
    if (success) {
      await Promise.all([
        loadJamChallengeDetails({ id: eventName, challengeId: params[1] }),
        loadJamChallengeData({ id: eventName, showLoader: false }),
        loadJamChallengeProgressData({ id: eventName, challengeId: params[1] }),
      ]);
      showProgressModalSet(true);
    }
    setLoading(false);
    setIsUserInitiatedValidation(false); // Reset flag
  };

  const validator = useMemo(
    () =>
      jamChallengeTaskAnswerValidator(
        answer,
        t(i18nKeys.myJams.challenges.details.task.answer.error),
        new Map<JamChallengeTaskAnswerFields, (error: string) => void>([
          [JamChallengeTaskAnswerFields.ANSWER, (error: string) => setAnswerError(error)],
        ])
      ),
    [answer, jamChallengeTaskAnswerValidator]
  );

  const initialize = async () => {
    setLoading(true);
    clearFlashbars();
    try {
      // If we don't have a challenge loaded yet, or the challenge loaded doesn't match the ID in the URL, reload it.
      if (!selectedJamChallenge || selectedJamChallenge?.id !== params[1]) {
        await Promise.all([
          loadJamChallengeDetails({ id: eventName, challengeId: params[1] }),
          isFacilitator ? loadChallengeSupportDetails(eventName, params[1]) : Promise.resolve(),
        ]);
      }
      // Fetch progress on mount and mark it resolved on success or failure, rather than trapping the user on a loader
      const progressChallengeId = params[1];
      void loadJamChallengeProgressData({ id: eventName, challengeId: progressChallengeId })
        .catch((e) => {
          LoggingService.debug('Error loading challenge progress:', e);
        })
        .finally(() => {
          setProgressResolvedChallengeId(progressChallengeId);
        });
    } catch (e) {
      LoggingService.debug('Error :', e);
    } finally {
      setLoading(false);
    }
  };

  // Re-init every time the challenge in the URL changes
  useEffect(() => {
    void initialize();
  }, [params[1]]);

  useEffect(() => {
    clearFlashbars();
    setAnswerError('');
    void getJamChallengeTask({ taskId: params[2] });
  }, [params[2], selectedJamChallenge?.id, selectedJamChallenge?.tasks]);

  useEffect(() => {
    if (!selectedJamChallenge && !isFetchingJamChallengeDetails) {
      history.push(JAM_CHALLENGE_DETAILS_ROUTES.Overview.resolve([eventSlug, params[1]]));
    }
  }, []);

  // Enforce lock/started on the task page so a locked or not-started challenge isn't exposed via direct URL, acting only once progress has settled.
  useEffect(() => {
    if (isFacilitator) {
      return;
    }
    if (isFetchingJamChallengeDetails || loading || !progressSettled) {
      return;
    }
    // Redirect when the challenge isn't viewable or the task doesn't exist in the loaded challenge.
    if (!canViewContent || !currentTaskExists) {
      history.push(JAM_CHALLENGE_DETAILS_ROUTES.Overview.resolve([eventSlug, params[1]]));
      addErrorFlashbar(t(i18nKeys.myJams.challenges.errors.startJamChallenge));
    }
  }, [
    canViewContent,
    currentTaskExists,
    isFacilitator,
    isFetchingJamChallengeDetails,
    loading,
    progressSettled,
    eventSlug,
    params[1],
  ]);

  const nextTaskId = useMemo(() => {
    if (selectedJamChallenge?.tasks?.length) {
      const currentTaskIndex = selectedJamChallenge.tasks.findIndex((currentTask) => currentTask.id === task?.id);
      if (currentTaskIndex > -1 && selectedJamChallenge.tasks.length > currentTaskIndex + 1) {
        return selectedJamChallenge.tasks[currentTaskIndex + 1].id;
      }
    }
  }, [selectedJamChallenge, task]);

  if (loading || fetchingtaskValidationResponse) {
    return <LoadingBar />;
  }

  // Block content until progress has settled, is viewable, and the loaded task matches the URL, so a locked/stale task never leaks for a frame.
  const blockContent = !isFacilitator && (!progressSettled || task?.id !== params[2] || !canViewContent);
  if (blockContent) {
    return <LoadingBar />;
  }

  return (
    <ContentLayout
      header={
        <Header variant="h1" description={t(i18nKeys.myJams.challenges.subtitle)}>
          {selectedJamChallenge?.title}
        </Header>
      }
    >
      <SpaceBetween direction="vertical" size="xxl">
        <JamChallengeHeader />
        <Box className="div" margin={{ bottom: 'xxl' }}>
          <Box variant="h2" margin={{ bottom: 's' }} fontSize="heading-xl">
            {t(i18nKeys.myJams.challenges.details.task.title)} {task?.taskNumber}: {task?.title ?? 'Untitled Task'}
          </Box>
          <SpaceBetween direction="vertical" size="xl">
            <TaskPointContainer
              task={task}
              handleProgressCheck={handleProgressCheck}
              handleAiTaskAudit={handleAiTaskAudit}
              disableProgressButton={showChallengeStatusProgress}
              disableAuditButton={disableAuditButton}
              loadingAudit={loadingAudit}
            />
            <Tabs
              tabs={[
                ...(task?.background
                  ? [
                      {
                        label: t(i18nKeys.myJams.challenges.tabs.background),
                        id: Background.title ?? 'background',
                        content: (
                          <Container>
                            <Markdown content={task?.background ?? ''} allowHtml />
                          </Container>
                        ),
                      },
                    ]
                  : []),
                ...(task?.inventory
                  ? [
                      {
                        label: t(i18nKeys.myJams.challenges.tabs.preRequisiteSetup),
                        id: PreRequisiteSetup.title ?? 'Pre-requisite setup',
                        content: (
                          <Container>
                            <Markdown content={task?.content ?? ''} allowHtml />
                          </Container>
                        ),
                      },
                    ]
                  : []),
                {
                  label: t(i18nKeys.myJams.challenges.tabs.taskAndClue),
                  id: `${t(i18nKeys.myJams.challenges.details.task.title)}-and-${t(
                    i18nKeys.myJams.challenges.details.task.clue.clues
                  )}`,
                  content: (
                    <SpaceBetween direction="vertical" size="xl">
                      <Form>
                        <Container className="dark-bg">
                          <SpaceBetween direction="vertical" size="l">
                            {task?.status !== JamTaskStatus.COMPLETE &&
                              task?.allowInputAnswer &&
                              challengeProgress?.started &&
                              !showChallengeStatusProgress && ( // if challenge deploy is in progress don't show submit answer
                                <SpaceBetween direction="horizontal" size="l" alignItems="start">
                                  <Box>
                                    <FormField errorText={answerError}>
                                      <Input
                                        data-testid="answerTextBox"
                                        value={answer}
                                        placeholder={t(i18nKeys.myJams.challenges.details.task.answer.placeholder)}
                                        onChange={handleInput}
                                        onBlur={() => validator.isValidField(JamChallengeTaskAnswerFields.ANSWER)}
                                      />
                                    </FormField>
                                  </Box>
                                  <Button
                                    variant="primary"
                                    data-testid="submitAnswerButton"
                                    disabled={!!answerError || !answer?.length}
                                    onClick={() => void submitAnswer(answer)}
                                  >
                                    {t(i18nKeys.myJams.challenges.tabs.buttons.submitAnswer)}
                                  </Button>
                                </SpaceBetween>
                              )}
                            <div className="task-markdown-container">
                              <Markdown content={task?.content} allowHtml />
                            </div>
                          </SpaceBetween>
                        </Container>
                      </Form>

                      {!!clues?.length && (
                        <GameTaskClues
                          isFacilitator={isFacilitator}
                          clues={clues}
                          disableUnlock={cluesFromSupportFallback}
                        />
                      )}
                    </SpaceBetween>
                  ),
                },
              ]}
              ariaLabel={t(i18nKeys.myJams.challenges.tabs.taskAndClue)}
            />
          </SpaceBetween>
        </Box>
      </SpaceBetween>
      <ProgressModal
        eventName={eventName}
        challengeId={selectedJamChallenge?.id || ''}
        nextTaskId={nextTaskId}
        showProgressModal={showProgressModal}
        challengeCompleted={!!challengeProgress?.completed}
        currentTaskCompleted={!!currentTaskProgress?.completed}
        validationMessage={validationMessage}
        onClose={handleSolvedModalClose}
        onDismiss={handleModalDismiss}
      />
      <JamTaskAuditModal
        message={auditMessage}
        showAuditModal={showAuditModal}
        onClose={() => setShowAuditModal(false)}
        onDismiss={() => setShowAuditModal(false)}
        data-testid="task-audit-modal"
      />
    </ContentLayout>
  );
};

interface ProgressModalProps {
  showProgressModal: boolean;
  currentTaskCompleted: boolean;
  challengeCompleted: boolean;
  validationMessage: string;
  onClose: () => void;
  onDismiss: () => void;
  nextTaskId?: string;
  eventName: string;
  challengeId: string;
}

const ProgressModal = ({
  showProgressModal,
  eventName,
  challengeId,
  nextTaskId,
  currentTaskCompleted,
  challengeCompleted,
  validationMessage,
  onClose,
  onDismiss,
}: ProgressModalProps) => {
  if (!showProgressModal) {
    return null;
  }
  if (challengeCompleted) {
    return (
      <ChallengeSolvedModal
        value={showProgressModal}
        message={validationMessage}
        onClose={onClose}
        onDismiss={onDismiss}
        data-testid="challenge-solved-modal"
      />
    );
  }
  if (currentTaskCompleted) {
    return (
      <JamTaskStatusModal
        isCompleted={currentTaskCompleted}
        onDismiss={onDismiss}
        message={validationMessage}
        onClose={onClose}
        nextTaskUrl={nextTaskId && JAM_CHALLENGE_TASK_ROUTES.resolve([eventName, challengeId, nextTaskId])}
        data-testid="task-status-modal"
      />
    );
  }
  return (
    <ChallengeNotCompleteModal
      value={showProgressModal}
      message={validationMessage}
      setValue={() => onDismiss()}
      data-testid="challenge-not-complete-modal"
    />
  );
};
